import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BiPlus, BiSearch, BiCalendar } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";

const OrganizationsArchive = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [billingStatusFilter, setBillingStatusFilter] = useState("");
  const pageSize = 10;

  const formatLabel = (value) => {
    if (!value) return "";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const fetchOrganizations = async (page = 1, search = "") => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/super-admin/organizations`, {
        params: {
          page,
          pageSize,
          search,
          plan_type: planTypeFilter || undefined,
          billing_status: billingStatusFilter || undefined
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Fetched Organizations:", response.data); // Debug log
      setOrganizations(response.data.data);
      setTotalPages(response.data.metadata.totalPages);
      setCurrentPage(response.data.metadata.currentPage);
    } catch (err) {
      setError("Failed to fetch organizations. Please try again.");
      console.error("Error fetching organizations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOrganizations(1, searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchOrganizations(1, searchQuery);
  }, [planTypeFilter, billingStatusFilter]);

  useEffect(() => {
    if (!searchQuery) {
      fetchOrganizations(currentPage, searchQuery);
    }
  }, [currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      fetchOrganizations(pageNumber, searchQuery);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const BillingDate = ({ org }) => {
    const status = org.billing_status;

    if (status === "past_due") {
      return (
        <div className="flex flex-col text-xs text-gray-600">
          <span className="text-red-600 font-medium">Card Payment Failed</span>
          <span>{org.subscription_ends_at ? "Service Ended: " + formatDate(org.subscription_ends_at) : "Trial Ended: " + formatDate(org.trial_ends_at)}</span>
        </div>
      );
    }

    if (status === "canceled") {
      return (
        <div className="flex flex-col text-xs text-gray-600">
          <span className="text-red-600 font-medium">Service Canceled</span>
          <span>{formatDate(org.subscription_ends_at)}</span>
        </div>
      );
    }

    if (status === "pending_cancellation") {
      return (
        <div className="flex flex-col text-xs text-gray-600">
          <span className="text-red-600 font-medium">Ends On:</span>
          <span>{formatDate(org.subscription_ends_at)}</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col text-xs text-gray-600">
        <span className="text-green-600 font-medium">Trial Ends</span>
        <span>{formatDate(org.trial_ends_at)}</span>
      </div>
    );
  };

  if (loading)
    return <div className="text-center text-gray-500 p-6">Loading organizations...</div>;
  if (error)
    return <div className="text-center text-red-500 p-6">{error}</div>;

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">

      {/* Header + Filters */}
      <div className="flex flex-col gap-4 mb-6">

        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">Organizations</h1>

          {/* Mobile Add Button */}
          <Link
            to="/super-admin/organizations/new"
            className="sm:hidden px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
          >
            <BiPlus className="inline mr-1" /> Add
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">

          {/* Search */}
          <div className="relative w-full sm:max-w-[260px]">
            <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Plan Filter */}
          <select
            value={planTypeFilter}
            onChange={(e) => setPlanTypeFilter(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Plans</option>
            <option value="standard">Standard</option>
            <option value="pro">Pro</option>
            <option value="elite">Elite</option>
          </select>

          {/* Billing Status Filter */}
          <select
            value={billingStatusFilter}
            onChange={(e) => setBillingStatusFilter(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="trialing">Trial</option>
            <option value="past_due">Past Due</option>
            <option value="pending_cancellation">Pending Cancellation</option>
            <option value="canceled">Canceled</option>
          </select>

          {/* Desktop Add Button */}
          <Link
            to="/super-admin/organizations/new"
            className="hidden sm:flex px-4 py-2 bg-blue-600 text-white rounded-lg text-sm items-center"
          >
            <BiPlus className="mr-1" /> Add New Org
          </Link>

        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-x-auto w-full">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-gray-600 font-semibold text-xs">Name / Admin</th>
              <th className="hidden sm:table-cell px-4 py-3 text-left text-gray-600 font-semibold text-xs">Plan</th>
              <th className="px-4 py-3 text-left text-gray-600 font-semibold text-xs">Status</th>
              <th className="hidden sm:table-cell px-4 py-3 text-left text-gray-600 font-semibold text-xs">Billing</th>
              <th className="hidden md:table-cell px-4 py-3 text-left text-gray-600 font-semibold text-xs">Next Date</th>
              <th className="px-4 py-3 text-right text-gray-600 font-semibold text-xs">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {organizations.length > 0 ? (
              organizations.map((org) => {
                const isActive =
                  org.billing_status === "active" || org.billing_status === "trialing";
                return (
                  <tr key={org.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <Link
                        to={`/organizations/view/${org.id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium block"
                      >
                        {org.name}
                      </Link>
                      <p className="text-xs text-gray-500">{org.admin_user_name || "N/A"}</p>
                      <p className="sm:hidden text-xs text-gray-400 mt-1">
                        Plan: {formatLabel(org.plan_type)}
                      </p>
                    </td>

                    <td className="hidden sm:table-cell px-4 py-4 text-gray-500">
                      {formatLabel(org.plan_type)}
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {formatLabel(org.billing_status)}
                      </span>
                    </td>

                    <td className="hidden sm:table-cell px-4 py-4 text-gray-500">
                      {org.billing_interval ? formatLabel(org.billing_interval) : "NA"}
                    </td>

                    <td className="hidden md:table-cell px-4 py-4">
                      <BillingDate org={org} />
                    </td>

                    <td className="px-4 py-4 text-right space-x-2">
                      <Link to={`/organizations/view/${org.id}`} className="text-blue-600">
                        View
                      </Link>
                      <Link
                        to={`/organizations/${org.id}`}
                        className="hidden sm:inline text-indigo-600"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  No organizations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-blue-600 text-white disabled:bg-gray-300"
          >
            Previous
          </button>

          <span className="text-gray-700 text-sm">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-blue-600 text-white disabled:bg-gray-300"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default OrganizationsArchive;
